import React from 'react';
import {
    Box,
    Stack,
    TextField,
    Dropdown,
    Button,
    Columns,
    Column,
    Text,
    Card,
    Divider,
    Actions,
    Dialog,
    Textarea,
    Drawer,
    IconAdd,
    Inline
} from 'braid-design-system';
import { useToast } from '../../hooks/useToast';
import { useMobileCustomer } from '../../contexts/ApproveMobileCustomerContext';
import { useAuth } from '../../contexts/AuthProvider';
import { getCustomerDetails, createCustomer, updateCustomer, uploadCustomerPhoto, uploadCustomerSignature, getCustomerTypeDetail, createCiCity } from '../../services/approveMobileCustomerService';
import CustomerDocsInfoForm from './CustomerDocsInfoForm';
import NicVerificationDrawer from './NicVerificationDrawer';
import { useTranslation } from 'react-i18next';
import DateField from '../DateField';
import Loading from "../../components/Loading";
import { isValidNicFormat, lookupNic, createNic, CiCustomerNicRecord } from '../../services/nicService';
import { config } from '../../config/app.config';

interface AddMemberFormProps {
    mode?: 'add' | 'update' | 'approve';
    customerNumber?: string; // Required for update/approve modes
    initialData?: Partial<{
        fullNameLn1: string;
        fullNameLn2: string;
        fullNameLn3: string;
        initialNameLn1: string;
        initialNameLn2: string;
        initialNameLn3: string;
        titleLn1: string;
        titleLn2: string;
        titleLn3: string;
        addressLn1: string;
        addressLn2: string;
        addressLn3: string;
        nic: string;
        gender: string;
        dateOfBirth: string;
        memberDate: string;
        marriedStatus: string;
        mobile1: string;
        mobile2: string;
        homePhone: string;
        email: string;
        rfidCard: string;
        creditLimit: string;
        creditLimitDate: string;
        status: string;
        inactiveReason: string;
        postalCode: string;
        cityId: string;
        groupId: string;
        smsStatus: string;
        dpAppStatus: string;
        posAppStatus: string;
        branch: string;
    }>;
    onSuccess?: () => void;
    onCancel?: () => void;
    onApprove?: () => void;
    onReject?: (reason: string) => void;
}

const AddMemberForm: React.FC<AddMemberFormProps> = ({
    mode = 'add',
    customerNumber,
    initialData = {},
    onSuccess,
    onCancel,
    onApprove,
    onReject
}) => {
    const toast = useToast();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { branches, customerTypes, ciCities, updateLoading, handleApprove, handleReject, addCityToState } = useMobileCustomer();

    // Loading state for fetching customer details
    const [fetchingData, setFetchingData] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    // Form state
    const [fullNameLn1, setFullNameLn1] = React.useState(initialData.fullNameLn1 || '');
    const [fullNameLn2, setFullNameLn2] = React.useState(initialData.fullNameLn2 || '');
    const [fullNameLn3, setFullNameLn3] = React.useState(initialData.fullNameLn3 || '');
    const [initialNameLn1, setInitialNameLn1] = React.useState(initialData.initialNameLn1 || '');
    const [initialNameLn2, setInitialNameLn2] = React.useState(initialData.initialNameLn2 || '');
    const [initialNameLn3, setInitialNameLn3] = React.useState(initialData.initialNameLn3 || '');
    const [titleLn1, setTitleLn1] = React.useState(initialData.titleLn1 || '');
    const [titleLn2, setTitleLn2] = React.useState(initialData.titleLn2 || '');
    const [titleLn3, setTitleLn3] = React.useState(initialData.titleLn3 || '');
    const [addressLn1, setAddressLn1] = React.useState(initialData.addressLn1 || '');
    const [addressLn2, setAddressLn2] = React.useState(initialData.addressLn2 || '');
    const [addressLn3, setAddressLn3] = React.useState(initialData.addressLn3 || '');
    const [nic, setNic] = React.useState(initialData.nic || '');
    const [gender, setGender] = React.useState(initialData.gender || '');
    const [dateOfBirth, setDateOfBirth] = React.useState(initialData.dateOfBirth || '');
    const [memberDate, setMemberDate] = React.useState(initialData.memberDate || '');
    const [marriedStatus, setMarriedStatus] = React.useState(initialData.marriedStatus || 'Single');
    const [mobile1, setMobile1] = React.useState(initialData.mobile1 || '');
    const [mobile2, setMobile2] = React.useState(initialData.mobile2 || '');
    const [homePhone, setHomePhone] = React.useState(initialData.homePhone || '');
    const [email, setEmail] = React.useState(initialData.email || '');
    const [rfidCard, setRfidCard] = React.useState(initialData.rfidCard || '');
    const [creditLimit, setCreditLimit] = React.useState(initialData.creditLimit || '');
    const [creditLimitDate, setCreditLimitDate] = React.useState(initialData.creditLimitDate || '');
    const [status, setStatus] = React.useState(initialData.status || '');
    const [inactiveReason, setInactiveReason] = React.useState(initialData.inactiveReason || '');
    const [postalCode, setPostalCode] = React.useState(initialData.postalCode || '');
    const [cityId, setCityId] = React.useState(initialData.cityId || '');
    const [groupId, setGroupId] = React.useState(initialData.groupId || '');
    const [smsStatus, setSmsStatus] = React.useState(initialData.smsStatus || '');
    const [dpAppStatus, setDpAppStatus] = React.useState(initialData.dpAppStatus || '');
    const [posAppStatus, setPosAppStatus] = React.useState(initialData.posAppStatus || '');
    const [branch, setBranch] = React.useState(initialData.branch || '');
    const [customerType, setCustomerType] = React.useState('');
    const [customerTypeNicMand, setCustomerTypeNicMand] = React.useState<number | null>(null);
    const [customerTypePhoneMand, setCustomerTypePhoneMand] = React.useState<number | null>(null);
    const [profileImage, setProfileImage] = React.useState<File | null>(null);
    const [signatureImage, setSignatureImage] = React.useState<File | null>(null);
    const [profileImagePreview, setProfileImagePreview] = React.useState<string | null>(null);
    const [signatureImagePreview, setSignatureImagePreview] = React.useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogImage, setDialogImage] = React.useState<{ url: string; title: string } | null>(null);
    const [rejectDialogOpen, setRejectDialogOpen] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('');
    const [docsInfoDrawerOpen, setDocsInfoDrawerOpen] = React.useState(false);

    const [cityDialogOpen, setCityDialogOpen] = React.useState(false);
    const [newDistrict, setNewDistrict] = React.useState('');
    const [newCityName, setNewCityName] = React.useState('');
    const [creatingCity, setCreatingCity] = React.useState(false);

    // NIC verification state
    const [nicValidating, setNicValidating] = React.useState(false);
    const [nicState, setNicState] = React.useState<'none' | 'verified' | 'unverified'>('none');
    const [nicRecord, setNicRecord] = React.useState<CiCustomerNicRecord | null>(null);
    const [nicDrawerOpen, setNicDrawerOpen] = React.useState(false);

    const nicValid = isValidNicFormat(nic);
    // NIC is only mandatory when the selected customer type requires it.
    const nicRequired = customerTypeNicMand === 1;
    // In 'add' mode:
    //  - if the customer type requires NIC, only Customer Type / Branch / NIC are shown until the NIC is verified.
    //  - if the customer type does NOT require NIC, show the full form immediately (no validation needed).
    const showRemaining = mode !== 'add' || nicState === 'verified' || (mode === 'add' && !!customerType && !nicRequired);
    // The NIC field is always shown, even when the selected customer type does not require it.
    const showNicField = true;

    const applyNicToForm = (record: CiCustomerNicRecord) => {
        setFullNameLn1(record.name?.en || '');
        setFullNameLn2(record.name?.si || '');
        setFullNameLn3(record.name?.ta || '');
        setAddressLn1(record.address?.en || '');
        setAddressLn2(record.address?.si || '');
        setAddressLn3(record.address?.ta || '');
        if (record.sex) {
            setGender(record.sex.charAt(0).toUpperCase() + record.sex.slice(1).toLowerCase());
        }
        if (record.date_of_birth) {
            setDateOfBirth(record.date_of_birth.slice(0, 10));
        }
    };

    // Fill the add form from an existing customer that shares the same NIC (like update mode).
    // Only fills fields the user can still edit; always creates a NEW customer on submit.
    const applyCustomerToForm = (customer: Record<string, any>) => {
        if (!customer) return;
        const formatDate = (date: string | null | undefined) => {
            if (!date) return '';
            try {
                const d = new Date(date);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            } catch {
                return '';
            }
        };
        setTitleLn1(customer.title_ln1 || '');
        setTitleLn2(customer.title_ln2 || '');
        setTitleLn3(customer.title_ln3 || '');
        setFullNameLn1(customer.full_name_ln1 || '');
        setFullNameLn2(customer.full_name_ln2 || '');
        setFullNameLn3(customer.full_name_ln3 || '');
        setInitialNameLn1(customer.ini_name_ln1 || '');
        setInitialNameLn2(customer.ini_name_ln2 || '');
        setInitialNameLn3(customer.ini_name_ln3 || '');
        setAddressLn1(customer.address_ln1 || '');
        setAddressLn2(customer.address_ln2 || '');
        setAddressLn3(customer.address_ln3 || '');
        if (customer.nic) setNic(customer.nic);
        if (customer.gender) setGender(customer.gender);
        if (customer.date_of_birth) setDateOfBirth(formatDate(customer.date_of_birth));
        if (customer.member_date) setMemberDate(formatDate(customer.member_date));
        if (customer.married_status) setMarriedStatus(customer.married_status);
        if (customer.mobile_1) setMobile1(customer.mobile_1);
        if (customer.mobile_2) setMobile2(customer.mobile_2);
        if (customer.home_phone) setHomePhone(customer.home_phone);
        if (customer.e_mail) setEmail(customer.e_mail);
        if ((customer as any).rfid_card) setRfidCard((customer as any).rfid_card);
        if (customer.credit_limit != null) setCreditLimit(customer.credit_limit?.toString() || '');
        if (customer.credit_limit_date) setCreditLimitDate(formatDate(customer.credit_limit_date));
        if (customer.postal_code) setPostalCode(customer.postal_code);
        if (customer.ci_city_id) setCityId(customer.ci_city_id?.toString() || '');
        if (customer.group_id) setGroupId(customer.group_id?.toString() || '');
        if (customer.sms_status != null) setSmsStatus(customer.sms_status?.toString() || '');
        if (customer.dp_app_status != null) setDpAppStatus(customer.dp_app_status?.toString() || '');
        if (customer.pos_app_status != null) setPosAppStatus(customer.pos_app_status?.toString() || '');
    };

    const handleValidateNic = async () => {
        if (!nicValid || !customerType) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.validation.customer_type_required', { defaultValue: 'Customer Type is required' }),
                t('page.approve_mobile_app_customer.form.validation.nic_invalid', { defaultValue: 'Please enter a valid NIC number (10 chars ending in V/X, or 12 digits).' })
            );
            return;
        }
        setNicValidating(true);
        try {
            const result = await lookupNic(nic);
            if (!result.found || !result.record) {
                // NIC not registered yet — create a new unverified record and open the
                // same verification drawer so it can be verified and inserted.
                const created = await createNic(nic);
                setNicRecord(created);
                setNicState('unverified');
                setNicDrawerOpen(true);
                return;
            }
            setNicRecord(result.record);
            // If an existing customer shares this NIC, prefill its editable fields.
            applyCustomerToForm(result.customer || {});
            if (result.record.is_verified === 1) {
                setNicState('verified');
                applyNicToForm(result.record);
                toast.showSuccess(
                    t('page.approve_mobile_app_customer.form.nic.verified.title', { defaultValue: 'NIC Verified' }),
                    t('page.approve_mobile_app_customer.form.nic.verified.message', { defaultValue: 'NIC verified. Details filled automatically.' })
                );
            } else {
                setNicState('unverified');
                setNicDrawerOpen(true);
            }
        } catch (error: any) {
            toast.showError(
                t('page.approve_mobile_app_customer.form.nic.lookup_error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile_app_customer.form.nic.lookup_error.message', { defaultValue: 'Failed to validate NIC.' })
            );
        } finally {
            setNicValidating(false);
        }
    };

    const handleNicVerified = (record: CiCustomerNicRecord) => {
        setNicRecord(record);
        setNicState('verified');
        applyNicToForm(record);
        setNicDrawerOpen(false);
        toast.showSuccess(
            t('page.approve_mobile_app_customer.form.nic.verified.title', { defaultValue: 'NIC Verified' }),
            t('page.approve_mobile_app_customer.form.nic.verified.message', { defaultValue: 'NIC details saved and verified.' })
        );
    };

    const districts = [
        '',
        'Ampara',
        'Anuradhapura',
        'Badulla',
        'Batticaloa',
        'Colombo',
        'Galle',
        'Gampaha',
        'Hambantota',
        'Jaffna',
        'Kalutara',
        'Kandy',
        'Kegalle',
        'Kilinochchi',
        'Kurunegala',
        'Mannar',
        'Matale',
        'Matara',
        'Moneragala',
        'Mullaitivu',
        'Nuwara Eliya',
        'Polonnaruwa',
        'Puttalam',
        'Ratnapura',
        'Trincomalee',
        'Vavuniya'
    ];

    const handleCreateCity = async () => {
        if (!newDistrict) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.dialog.city.title', { defaultValue: 'Add City' }),
                t('page.approve_mobile_app_customer.form.dialog.city.validation.district_required', { defaultValue: 'Please select a district' })
            );
            return;
        }
        if (!newCityName.trim()) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.dialog.city.title', { defaultValue: 'Add City' }),
                t('page.approve_mobile_app_customer.form.dialog.city.validation.name_required', { defaultValue: 'Please enter a city name' })
            );
            return;
        }

        try {
            setCreatingCity(true);
            const newCity = await createCiCity(newDistrict, newCityName.trim());
            if (newCity) {
                addCityToState(newCity);
                setCityId(newCity.id.toString());
                setNewDistrict('');
                setNewCityName('');
                setCityDialogOpen(false);
                toast.showSuccess(
                    t('page.approve_mobile_app_customer.form.dialog.city.success.title', { defaultValue: 'Success' }),
                    t('page.approve_mobile_app_customer.form.dialog.city.success.message', { defaultValue: 'City created successfully' })
                );
            }
        } catch (error: any) {
            toast.showError(
                t('page.approve_mobile_app_customer.form.dialog.city.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile_app_customer.form.dialog.city.error.message', { defaultValue: 'Failed to create city' })
            );
        } finally {
            setCreatingCity(false);
        }
    };
    const [createdCustomerId, setCreatedCustomerId] = React.useState<number | null>(null);
    const [createdCustomerTypeId, setCreatedCustomerTypeId] = React.useState<number | null>(null);
    const [loadedCustomerId, setLoadedCustomerId] = React.useState<number | null>(null);
    const [loadedCustomerTypeId, setLoadedCustomerTypeId] = React.useState<number | null>(null);

    const isReadOnly = mode === 'approve';


    // Field disabling logic
    // In 'approve' mode: all fields disabled
    // In 'update' mode: specific fields disabled (fullName, initialName, title, gender, dateOfBirth, memberDate, marriedStatus)
    const isFieldDisabled = (fieldName: string) => {
        if (mode === 'approve') return true;
        if (mode === 'update') {
            const disabledFields = ['fullName', 'initialName', 'title', 'memberDate'];
            return disabledFields.includes(fieldName);
        }
        return false;
    };

    const titleLn1Field = (
        <Dropdown
            label={t('page.approve_mobile_app_customer.form.field.title_ln1', { defaultValue: 'Title (English) *' })}
            value={titleLn1}
            onChange={(e) => setTitleLn1(e.currentTarget.value)}
            disabled={isFieldDisabled('titleLn1')}
        >
            <option value="">{t('page.approve_mobile_app_customer.form.placeholder.select_title_ln1', { defaultValue: 'Select Title Ln1' })}</option>
            <option value="Mr">{t('page.approve_mobile_app_customer.form.option.title_ln1.mr', { defaultValue: 'Mr' })}</option>
            <option value="Mrs">{t('page.approve_mobile_app_customer.form.option.title_ln1.mrs', { defaultValue: 'Mrs' })}</option>
            <option value="Miss">{t('page.approve_mobile_app_customer.form.option.title_ln1.miss', { defaultValue: 'Miss' })}</option>
            <option value="Dr">{t('page.approve_mobile_app_customer.form.option.title_ln1.dr', { defaultValue: 'Dr' })}</option>
            <option value="Rev">{t('page.approve_mobile_app_customer.form.option.title_ln1.rev', { defaultValue: 'Rev' })}</option>
        </Dropdown>
    );

    const isValidPhone = (value: string, allowEmpty = true) => {
        if (!value) return allowEmpty;
        return /^(0\d{9}|7\d{8})$/.test(value);
    };

    // Fetch customer details when in update or approve mode
    React.useEffect(() => {
        const loadCustomerData = async () => {
            if ((mode === 'update' || mode === 'approve') && customerNumber) {
                try {
                    setFetchingData(true);
                    const customerData = await getCustomerDetails(customerNumber);

                    // Helper to format date for input fields (local timezone, not UTC)
                    const formatDate = (date: string | null | undefined) => {
                        if (!date) return '';
                        try {
                            const d = new Date(date);
                            const year = d.getFullYear();
                            const month = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${year}-${month}-${day}`;
                        } catch {
                            return '';
                        }
                    };

                    // Populate form fields with fetched data
                    setFullNameLn1(customerData.full_name_ln1 || '');
                    setFullNameLn2(customerData.full_name_ln2 || '');
                    setFullNameLn3(customerData.full_name_ln3 || '');
                    setInitialNameLn1(customerData.ini_name_ln1 || '');
                    setInitialNameLn2(customerData.ini_name_ln2 || '');
                    setInitialNameLn3(customerData.ini_name_ln3 || '');
                    setTitleLn1(customerData.title_ln1 || '');
                    setTitleLn2(customerData.title_ln2 || '');
                    setTitleLn3(customerData.title_ln3 || '');
                    setAddressLn1(customerData.address_ln1 || '');
                    setAddressLn2(customerData.address_ln2 || '');
                    setAddressLn3(customerData.address_ln3 || '');
                    setNic(customerData.nic || '');
                    setGender(customerData.gender || '');
                    setDateOfBirth(formatDate(customerData.date_of_birth));
                    setMemberDate(formatDate(customerData.member_date));
                    setMarriedStatus(customerData.married_status || '');
                    setMobile1(customerData.mobile_1 || '');
                    setMobile2(customerData.mobile_2 || '');
                    setHomePhone(customerData.home_phone || '');
                    setEmail(customerData.e_mail || '');
                    setRfidCard((customerData as any).rfid_card || '');
                    setCreditLimit(customerData.credit_limit?.toString() || '');
                    setCreditLimitDate(formatDate(customerData.credit_limit_date));
                    setStatus(customerData.status?.toString() || '');
                    setInactiveReason(customerData.inactive_reson || '');
                    setPostalCode(customerData.postal_code || '');
                    setCityId(customerData.ci_city_id?.toString() || '');
                    setGroupId(customerData.group_id?.toString() || '');
                    setSmsStatus(customerData.sms_status?.toString() || '');
                    setDpAppStatus(customerData.dp_app_status?.toString() || '');
                    setPosAppStatus(customerData.pos_app_status?.toString() || '');
                    setBranch(customerData.branch_id?.toString() || '');
                    // fetch nic_mand for loaded customer type if available
                    if (customerData.customer_type_id) {
                        try {
                            const detail = await getCustomerTypeDetail(customerData.customer_type_id);
                            setCustomerTypeNicMand(detail?.nic_mand ?? null);
                            setCustomerTypePhoneMand(detail?.phone_mand ?? null);
                        } catch (err) {
                            // ignore detail fetch error
                        }
                    }
                    setProfileImagePreview(customerData.customer_number ? `${config.apiUrl}/customer/${customerData.customer_number}/photo` : null);
                    setSignatureImagePreview(customerData.customer_number ? `${config.apiUrl}/customer/${customerData.customer_number}/signature` : null);

                    // Store customer ID and customer type ID for update mode
                    if (mode === 'update') {
                        setLoadedCustomerId(customerData.id || null);
                        setLoadedCustomerTypeId(customerData.customer_type_id || null);
                    }

                    // TODO: Handle photo and signature preview if needed

                } catch (error: any) {
                    toast.showError(t('page.approve_mobile_app_customer.form.error.load_customer', { defaultValue: 'Failed to load customer data' }));
                } finally {
                    setFetchingData(false);
                }
            }
        };

        loadCustomerData();
    }, [mode, customerNumber, toast]);

    const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setProfileImage(file);

            if (profileImagePreview) {
                URL.revokeObjectURL(profileImagePreview);
            }
            const previewUrl = URL.createObjectURL(file);
            setProfileImagePreview(previewUrl);
        }
    };

    // When customer type selection changes, fetch customer type details to determine `nic_mand`
    React.useEffect(() => {
        let cancelled = false;
        const fetchNicMand = async (typeIdStr: string) => {
            const id = Number(typeIdStr);
            if (!id) {
                setCustomerTypeNicMand(null);
                setCustomerTypePhoneMand(null);
                return;
            }
            try {
                const detail = await getCustomerTypeDetail(id);
                if (cancelled) return;
                setCustomerTypeNicMand(detail?.nic_mand ?? null);
                setCustomerTypePhoneMand(detail?.phone_mand ?? null);
            } catch (err) {
                if (cancelled) return;
                setCustomerTypeNicMand(null);
                setCustomerTypePhoneMand(null);
            }
        };

        fetchNicMand(customerType);

        return () => {
            cancelled = true;
        };
    }, [customerType]);

    const handleSignatureImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSignatureImage(file);

            if (signatureImagePreview) {
                URL.revokeObjectURL(signatureImagePreview);
            }
            const previewUrl = URL.createObjectURL(file);
            setSignatureImagePreview(previewUrl);
        }
    };

    const openImageDialog = (url: string, title: string) => {
        setDialogImage({ url, title });
        setDialogOpen(true);
    };

    React.useEffect(() => {
        return () => {
            if (profileImagePreview) {
                URL.revokeObjectURL(profileImagePreview);
            }
            if (signatureImagePreview) {
                URL.revokeObjectURL(signatureImagePreview);
            }
        };
    }, [profileImagePreview, signatureImagePreview]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validations = [
            { value: fullNameLn1, message: t('page.approve_mobile_app_customer.form.validation.full_name_required', { defaultValue: 'Full Name Ln1 is required' }) },
            // NIC required only when customer type requires it (nic_mand === 1)
            ...(customerTypeNicMand === 1 ? [{ value: nic, message: t('page.approve_mobile_app_customer.form.validation.nic_required', { defaultValue: 'NIC is required' }) }] : []),
            ...(customerTypePhoneMand === 1 ? [{ value: mobile1, message: t('page.approve_mobile_app_customer.form.validation.mobile1_required', { defaultValue: 'Mobile number 1 is required' }) }] : []),
            { value: titleLn1, message: t('page.approve_mobile_app_customer.form.validation.title_required', { defaultValue: 'Title is required' }) },
            { value: addressLn1, message: t('page.approve_mobile_app_customer.form.validation.address_required', { defaultValue: 'Address is required' }) },
            { value: initialNameLn1, message: t('page.approve_mobile_app_customer.form.validation.initial_name_required', { defaultValue: 'Initial Name Ln1 is required' }) },
            { value: gender, message: t('page.approve_mobile_app_customer.form.validation.gender_required', { defaultValue: 'Gender is required' }) },
            { value: dateOfBirth, message: t('page.approve_mobile_app_customer.form.validation.dob_required', { defaultValue: 'Date of Birth is required' }) },
            { value: memberDate, message: t('page.approve_mobile_app_customer.form.validation.member_date_required', { defaultValue: 'Member Date is required' }) },
            { value: marriedStatus, message: t('page.approve_mobile_app_customer.form.validation.married_status_required', { defaultValue: 'Married Status is required' }) },
            // { value: mobile1, message: t('page.approve_mobile_app_customer.form.validation.mobile1_required', { defaultValue: 'Mobile number 1 is required' }) },
            ...(customerTypePhoneMand === 1 ? [{ value: isValidPhone(mobile1, false), message: t('page.approve_mobile_app_customer.form.validation.mobile1_invalid', { defaultValue: 'Mobile number 1 must be 10 digits and start with 0 or must be 9 digits and start with 7' }) }] : []),
            { value: isValidPhone(mobile2, true), message: t('page.approve_mobile_app_customer.form.validation.mobile2_invalid', { defaultValue: 'Mobile number 2 must be 10 digits and start with 0 or must be 9 digits and start with 7' }) },
            { value: isValidPhone(homePhone, true), message: t('page.approve_mobile_app_customer.form.validation.home_phone_invalid', { defaultValue: 'Home phone must be 10 digits and start with 0 or must be 9 digits and start with 7' }) },
            { value: cityId, message: t('page.approve_mobile_app_customer.form.validation.city_required', { defaultValue: 'City is required' }) },
        ];

        if (mode === 'add') {
            validations.push({
                value: customerType,
                message: t('page.approve_mobile_app_customer.form.validation.customer_type_required', { defaultValue: 'Customer Type is required' })
            });
        }

        for (const field of validations) {
            if (!field.value || !field.value.toString().trim()) {
                toast.showError(field.message);
                return;
            }
        }

        // Limit dates to current login year (no old/previous years)
        // const currentYearVal = new Date().getFullYear();
        // if (memberDate) {
        //     const memberYear = new Date(memberDate).getFullYear();
        //     if (memberYear < currentYearVal) {
        //         toast.showError(t('page.approve_mobile_app_customer.form.validation.member_date_old_year', { defaultValue: 'Member Date cannot be in a previous year.' }));
        //         return;
        //     }
        // }
        // if (creditLimitDate) {
        //     const creditYear = new Date(creditLimitDate).getFullYear();
        //     if (creditYear < currentYearVal) {
        //         toast.showError(t('page.approve_mobile_app_customer.form.validation.credit_limit_date_old_year', { defaultValue: 'Credit Limit Date cannot be in a previous year.' }));
        //         return;
        //     }
        // }

        // Map frontend field names to backend schema
        const payload = {
            customer_type_id: customerType ? Number(customerType) : undefined,
            title_ln1: titleLn1,
            title_ln2: titleLn2 || undefined,
            title_ln3: titleLn3 || undefined,
            full_name_ln1: fullNameLn1,
            full_name_ln2: fullNameLn2 || undefined,
            full_name_ln3: fullNameLn3 || undefined,
            ini_name_ln1: initialNameLn1,
            ini_name_ln2: initialNameLn2 || undefined,
            ini_name_ln3: initialNameLn3 || undefined,
            address_ln1: addressLn1,
            address_ln2: addressLn2 || undefined,
            address_ln3: addressLn3 || undefined,
            nic: nic || undefined,
            gender: gender,
            date_of_birth: dateOfBirth || undefined,
            member_date: memberDate || undefined,
            married_status: marriedStatus,
            mobile_1: mobile1 || undefined,
            mobile_2: mobile2 || undefined,
            home_phone: homePhone || undefined,
            e_mail: email || undefined,
            rfid_card: rfidCard || undefined,
            credit_limit: creditLimit ? Number(creditLimit) : undefined,
            credit_limit_date: creditLimitDate || undefined,
            inactive_reson: inactiveReason || undefined,
            postal_code: postalCode || undefined,
            ci_city_id: cityId ? Number(cityId) : undefined,
            group_id: groupId ? Number(groupId) : undefined,
            sms_status: smsStatus ? Number(smsStatus) : 0,
            dp_app_status: dpAppStatus ? Number(dpAppStatus) : 0,
            pos_app_status: posAppStatus ? Number(posAppStatus) : 0,
            ci_customer_nic_id: nicRecord?.id ?? undefined,
        };

        try {
            setLoading(true);
            if (mode === 'add') {
                // Create customer and get the response with customer ID
                const result = await createCustomer(payload);
                const createdCustomerNumber = result.customer_number;

                // Upload profile image and signature if provided
                if (profileImage && createdCustomerNumber) {
                    try {
                        await uploadCustomerPhoto(createdCustomerNumber, profileImage);
                    } catch (error: any) {
                        toast.showError(t('page.approve_mobile_app_customer.form.error.upload_photo', { defaultValue: 'Failed to upload profile photo' }));
                    }
                }

                if (signatureImage && createdCustomerNumber) {
                    try {
                        await uploadCustomerSignature(createdCustomerNumber, signatureImage);
                    } catch (error: any) {
                        toast.showError(t('page.approve_mobile_app_customer.form.error.upload_signature', { defaultValue: 'Failed to upload signature' }));
                    }
                }

                setCreatedCustomerId(result.customer_id);
                setCreatedCustomerTypeId(result.customer_type_id);
                setDocsInfoDrawerOpen(true);
                toast.showSuccess(t('page.approve_mobile_app_customer.form.toast.customer_create_success', { defaultValue: 'Customer created successfully' }));
            } else if (mode === 'update' && customerNumber) {
                await updateCustomer(customerNumber, payload);

                // Upload profile image and signature if provided
                if (profileImage) {
                    try {
                        await uploadCustomerPhoto(customerNumber, profileImage);
                    } catch (error: any) {
                        toast.showError(t('page.approve_mobile_app_customer.form.error.upload_photo', { defaultValue: 'Failed to upload profile photo' }));
                    }
                }

                if (signatureImage) {
                    try {
                        await uploadCustomerSignature(customerNumber, signatureImage);
                    } catch (error: any) {
                        toast.showError(t('page.approve_mobile_app_customer.form.error.upload_signature', { defaultValue: 'Failed to upload signature' }));
                    }
                }

                // Use the customer ID and type ID that we loaded earlier
                if (loadedCustomerId && loadedCustomerTypeId) {
                    setCreatedCustomerId(loadedCustomerId);
                    setCreatedCustomerTypeId(loadedCustomerTypeId);
                    setDocsInfoDrawerOpen(true);
                } else {
                    // Fallback: call onSuccess if IDs are not available
                    if (onSuccess) {
                        onSuccess();
                    }
                }
                toast.showSuccess(t('page.approve_mobile_app_customer.form.toast.customer_update_success', { defaultValue: 'Customer created successfully' }));
            }
        } catch (error: any) {
            toast.showError(error.message || t('page.approve_mobile_app_customer.form.error.save_customer', { defaultValue: 'Failed to save customer' }));
        } finally {
            setLoading(false);
        }
    };

    const handleApproveClick = async () => {
        if (!customerNumber) {
            toast.showError(t('page.approve_mobile_app_customer.form.error.approval_missing_number', { defaultValue: 'Customer number is required for approval' }));
            return;
        }

        try {
            await handleApprove(customerNumber);
            if (onApprove) {
                onApprove();
            }
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            // Error already handled by context
        }
    };

    const handleRejectClick = () => {
        setRejectDialogOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!customerNumber) {
            toast.showError(t('page.approve_mobile_app_customer.form.error.rejection_missing_number', { defaultValue: 'Customer number is required for rejection' }));
            return;
        }

        if (!rejectReason.trim()) {
            toast.showError(t('page.approve_mobile_app_customer.form.validation.reject_reason_required', { defaultValue: 'Please provide a reason for rejection' }));
            return;
        }

        try {
            await handleReject(customerNumber, { deactivate_id: 1, inactive_reason: rejectReason });
            if (onReject) {
                onReject(rejectReason);
            }
            toast.showInfo(t('page.approve_mobile_app_customer.form.toast.rejected', {
                defaultValue: 'Member rejected. Reason: {{reason}}',
                reason: rejectReason
            }));
            setRejectDialogOpen(false);
            setRejectReason('');

            if (onSuccess) {
                onSuccess();
            }

        } catch (error) {

        }

    };

    const handleCancelReject = () => {
        setRejectDialogOpen(false);
        setRejectReason('');
    };

    // Show loading indicator while fetching customer data
    if (fetchingData) {
        return (
            <Box padding="medium">
                <Stack space="large" align="center">
                    <Text>{t('page.approve_mobile_app_customer.form.loading_customer', { defaultValue: 'Loading customer data...' })}</Text>
                </Stack>
            </Box>
        );
    }

    if (loading) {
        return (
            <Loading message={mode === 'add' ? t('page.approve_mobile_app_customer.form.creating_customer', { defaultValue: 'Creating customer...' }) : t('page.approve_mobile_app_customer.form.updating_customer', { defaultValue: 'Updating customer...' })} />
        );
    }

    return (
        <Box padding="medium">
            <style>{`.sinhala-input input { font-family: 'Iskoola Pota', 'Noto Sans Sinhala', sans-serif !important; }`}</style>
            <form onSubmit={handleSubmit} autoComplete="off">
                <Stack space="large">

                    <Divider />

                    {/* Personal Information */}
                    <Stack space="medium">
                        <Text weight="strong">{t('page.approve_mobile_app_customer.form.section.personal_information', { defaultValue: 'Personal Information' })}</Text>

                        <Columns space="small" collapseBelow="tablet">
                            {/* Customer Type Selection - Required for new members only */}
                            <Column>
                                {mode === 'add' ? (
                                    <Dropdown
                                        label={t('page.approve_mobile_app_customer.form.field.customer_type', { defaultValue: 'Customer Type *' })}
                                        value={customerType}
                                        onChange={(e) => setCustomerType(e.currentTarget.value)}
                                    >
                                        {customerTypes.map((ct) => (
                                            <option key={ct.id} value={ct.id.toString()}>
                                                {ct.type_ln1}
                                            </option>
                                        ))}
                                    </Dropdown>
                                ) : (
                                    <Box />
                                )}
                            </Column>
                            {/* Branch Selection - Required for new members, read-only for update/approve */}
                            <Column>
                                {mode === 'add' ? (
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.branch', { defaultValue: 'Branch *' })}
                                        value={branches.find(b => b.id.toString() === user?.active_branch_id?.toString())?.name_ln1 || 'N/A'}
                                        disabled
                                        onChange={(e) => setBranch(e.currentTarget.value)}
                                    />
                                ) : (
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.branch', { defaultValue: 'Branch *' })}
                                        value={branches.find(b => b.id.toString() === branch)?.name_ln1 || 'N/A'}
                                        disabled
                                        onChange={(e) => setBranch(e.currentTarget.value)}
                                    />
                                )}
                            </Column>
                        </Columns>

                        {showNicField ? (
                            <Columns space="small" collapseBelow="tablet">
                                <Column>
                                    <Inline space="small" alignY="center">
                                        <Box>
                                            {nicRequired ? (
                                                <TextField
                                                    label={t('page.approve_mobile_app_customer.form.field.nic_required', { defaultValue: 'NIC *' })}
                                                    value={nic}
                                                    onChange={(e) => {
                                                        let value = e.currentTarget.value.toUpperCase();
                                                        value = value.replace(/[^0-9VX]/g, '');
                                                        if (value.length > 12) return;
                                                        setNic(value);
                                                        if (nicState !== 'none') setNicState('none');
                                                    }}
                                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.nic', { defaultValue: 'Enter NIC number' })}
                                                    disabled={isFieldDisabled('nic')}
                                                    characterLimit={12}
                                                />
                                            ) : (
                                                <TextField
                                                    label={t('page.approve_mobile_app_customer.form.field.nic_optional', { defaultValue: 'NIC' })}
                                                    value={nic}
                                                    onChange={(e) => {
                                                        let value = e.currentTarget.value.toUpperCase();
                                                        value = value.replace(/[^0-9VX]/g, '');
                                                        if (value.length > 12) return;
                                                        setNic(value);
                                                        if (nicState !== 'none') setNicState('none');
                                                    }}
                                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.nic', { defaultValue: 'Enter NIC number' })}
                                                    disabled={isFieldDisabled('nic')}
                                                    characterLimit={12}
                                                />
                                            )}
                                        </Box>
                                        {mode === 'add' && nicRequired && !!customerType && nicValid && nicState === 'none' && (
                                            <Button
                                                onClick={handleValidateNic}
                                                loading={nicValidating}
                                                tone="brandAccent"
                                            >
                                                    {t('page.approve_mobile_app_customer.form.button.validate_nic', { defaultValue: 'Validate' })}
                                                </Button>
                                        )}
                                    </Inline>
                                </Column>
                                {showRemaining ? (
                                    <Column>
                                        {titleLn1Field}
                                    </Column>
                                ) : null}
                            </Columns>
                        ) : showRemaining ? (
                            <Box>
                                {titleLn1Field}
                            </Box>
                        ) : null}

                        {showRemaining && (
                            <>
                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.title_ln2', { defaultValue: 'Title (Sinhala)' })}
                                    value={titleLn2}
                                    onChange={(e) => setTitleLn2(e.currentTarget.value)}
                                    disabled={isFieldDisabled('titleLn2')}
                                >
                                    <option value="">{t('page.approve_mobile_app_customer.form.placeholder.select_title_ln2', { defaultValue: 'Select Title Ln2' })}</option>
                                    <option value="මහතා">{t('page.approve_mobile_app_customer.form.option.title_ln2.mr', { defaultValue: 'Mr' })}</option>
                                    <option value="මහත්මිය">{t('page.approve_mobile_app_customer.form.option.title_ln2.mrs', { defaultValue: 'Mrs' })}</option>
                                    <option value="මිස්">{t('page.approve_mobile_app_customer.form.option.title_ln2.miss', { defaultValue: 'Miss' })}</option>
                                    <option value="වෛද්‍ය">{t('page.approve_mobile_app_customer.form.option.title_ln2.dr', { defaultValue: 'Dr' })}</option>
                                    <option value="පූජ්‍ය">{t('page.approve_mobile_app_customer.form.option.title_ln2.rev', { defaultValue: 'Rev' })}</option>
                                </Dropdown>
                            </Column>
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.title_ln3', { defaultValue: 'Title (Tamil)' })}
                                    value={titleLn3}
                                    onChange={(e) => setTitleLn3(e.currentTarget.value)}
                                    disabled={isFieldDisabled('titleLn3')}
                                >
                                    <option value="">{t('page.approve_mobile_app_customer.form.placeholder.select_title_ln3', { defaultValue: 'Select Title Ln3' })}</option>
                                    <option value="திரு">{t('page.approve_mobile_app_customer.form.option.title_ln3.mr', { defaultValue: 'Mr' })}</option>
                                    <option value="திருமதி">{t('page.approve_mobile_app_customer.form.option.title_ln3.mrs', { defaultValue: 'Mrs' })}</option>
                                    <option value="செல்வி">{t('page.approve_mobile_app_customer.form.option.title_ln3.miss', { defaultValue: 'Miss' })}</option>
                                    <option value="டாக்டர்">{t('page.approve_mobile_app_customer.form.option.title_ln3.dr', { defaultValue: 'Dr' })}</option>
                                    <option value="திரு. பணி">{t('page.approve_mobile_app_customer.form.option.title_ln3.rev', { defaultValue: 'Rev' })}</option>
                                </Dropdown>
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.full_name_ln1', { defaultValue: 'Full Name (English) *' })}
                                    value={fullNameLn1}
                                    onChange={(e) => setFullNameLn1(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.full_name_ln1', { defaultValue: 'Enter full name' })}
                                    disabled={isFieldDisabled('fullNameLn1')}
                                />
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.initial_name_ln1', { defaultValue: 'Initial Name (English) *' })}
                                    value={initialNameLn1}
                                    onChange={(e) => setInitialNameLn1(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.initial_name_ln1', { defaultValue: 'Enter initial name' })}
                                    disabled={isFieldDisabled('initialNameLn1')}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <div className="sinhala-input">
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.full_name_ln2', { defaultValue: 'Full Name (Sinhala)' })}
                                        value={fullNameLn2}
                                        onChange={(e) => setFullNameLn2(e.currentTarget.value)}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.full_name_ln2', { defaultValue: 'Enter full name' })}
                                        disabled={isFieldDisabled('fullNameLn2')}
                                    />
                                </div>
                            </Column>
                            <Column>
                                <div className="sinhala-input">
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.initial_name_ln2', { defaultValue: 'Initial Name (Sinhala)' })}
                                        value={initialNameLn2}
                                        onChange={(e) => setInitialNameLn2(e.currentTarget.value)}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.initial_name_ln2', { defaultValue: 'Enter initial name' })}
                                        disabled={isFieldDisabled('initialNameLn2')}
                                    />
                                </div>
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.full_name_ln3', { defaultValue: 'Full Name (Tamil)' })}
                                    value={fullNameLn3}
                                    onChange={(e) => setFullNameLn3(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.full_name_ln3', { defaultValue: 'Enter full name' })}
                                    disabled={isFieldDisabled('fullNameLn3')}
                                />
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.initial_name_ln3', { defaultValue: 'Initial Name (Tamil)' })}
                                    value={initialNameLn3}
                                    onChange={(e) => setInitialNameLn3(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.initial_name_ln3', { defaultValue: 'Enter initial name' })}
                                    disabled={isFieldDisabled('initialNameLn3')}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.address_ln1', { defaultValue: 'Address (English) *' })}
                                    value={addressLn1}
                                    onChange={(e) => setAddressLn1(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.address_ln1', { defaultValue: 'Enter address' })}
                                    disabled={isFieldDisabled('addressLn1')}
                                />
                            </Column>
                            <Column>
                                <div className="sinhala-input">
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.address_ln2', { defaultValue: 'Address (Sinhala)' })}
                                        value={addressLn2}
                                        onChange={(e) => setAddressLn2(e.currentTarget.value)}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.address_ln2', { defaultValue: 'Enter address' })}
                                        disabled={isFieldDisabled('addressLn2')}
                                    />
                                </div>
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.address_ln3', { defaultValue: 'Address (Tamil)' })}
                                    value={addressLn3}
                                    onChange={(e) => setAddressLn3(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.address_ln3', { defaultValue: 'Enter address' })}
                                    disabled={isFieldDisabled('addressLn3')}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.gender', { defaultValue: 'Gender *' })}
                                    value={gender}
                                    onChange={(e) => setGender(e.currentTarget.value)}
                                    disabled={isFieldDisabled('gender')}
                                >
                                    <option value="">{t('page.approve_mobile_app_customer.form.placeholder.select_gender', { defaultValue: 'Select Gender' })}</option>
                                    <option value="Male">{t('page.approve_mobile_app_customer.form.option.gender.male', { defaultValue: 'Male' })}</option>
                                    <option value="Female">{t('page.approve_mobile_app_customer.form.option.gender.female', { defaultValue: 'Female' })}</option>
                                    <option value="Other">{t('page.approve_mobile_app_customer.form.option.gender.other', { defaultValue: 'Other' })}</option>
                                </Dropdown>
                            </Column>
                            <Column>
                                <DateField
                                    label={t('page.approve_mobile_app_customer.form.field.dob', { defaultValue: 'Date of Birth *' })}
                                    onlyForToday={true}
                                    onChange={(value) => setDateOfBirth(value)}
                                    value={dateOfBirth ? dateOfBirth.slice(0, 10) : ''}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                                    disabled={isFieldDisabled('dateOfBirth')}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <DateField
                                    label={t('page.approve_mobile_app_customer.form.field.member_date', { defaultValue: 'Member Date *' })}
                                    onlyForToday={true}
                                    onChange={(value) => setMemberDate(value)}
                                    value={memberDate ? memberDate.slice(0, 10) : ''}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                                    disabled={isFieldDisabled('memberDate')}
                                />
                            </Column>
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.married_status', { defaultValue: 'Married Status *' })}
                                    value={marriedStatus}
                                    onChange={(e) => setMarriedStatus(e.currentTarget.value)}
                                    disabled={isFieldDisabled('marriedStatus')}
                                >
                                    <option value="Single">{t('page.approve_mobile_app_customer.form.option.married_status.single', { defaultValue: 'Single' })}</option>
                                    <option value="Married">{t('page.approve_mobile_app_customer.form.option.married_status.married', { defaultValue: 'Married' })}</option>
                                    <option value="Divorced">{t('page.approve_mobile_app_customer.form.option.married_status.divorced', { defaultValue: 'Divorced' })}</option>
                                    <option value="Widowed">{t('page.approve_mobile_app_customer.form.option.married_status.widowed', { defaultValue: 'Widowed' })}</option>
                                </Dropdown>
                            </Column>
                        </Columns>
                            </>)}
                    </Stack>

                    {showRemaining && (
                        <>
                    <Divider />

                    {/* Contact Information */}
                    <Stack space="medium">
                        <Text weight="strong">{t('page.approve_mobile_app_customer.form.section.contact_information', { defaultValue: 'Contact Information' })}</Text>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                {customerTypePhoneMand === 1 ? (
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.mobile1_required', { defaultValue: 'Mobile 1 *' })}
                                        value={mobile1}
                                        onChange={(e) => {
                                            const value = e.currentTarget.value;
                                            if (!/^\d*$/.test(value)) return;
                                            if (value.length === 1 && value !== '0') return;
                                            if (value.length > 10) return;
                                            setMobile1(value);
                                        }}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.mobile', { defaultValue: '0XXXXXXXXX' })}
                                        type="tel"
                                        disabled={isFieldDisabled('mobile1')}
                                        characterLimit={10}
                                    />
                                ) : (
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.mobile1_optional', { defaultValue: 'Mobile 1' })}
                                        value={mobile1}
                                        onChange={(e) => {
                                            const value = e.currentTarget.value;
                                            if (!/^\d*$/.test(value)) return;
                                            if (value.length === 1 && value !== '0') return;
                                            if (value.length > 10) return;
                                            setMobile1(value);
                                        }}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.mobile', { defaultValue: '0XXXXXXXXX' })}
                                        type="tel"
                                        disabled={isFieldDisabled('mobile1')}
                                        characterLimit={10}
                                    />
                                )}
                                
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.mobile2', { defaultValue: 'Mobile 2' })}
                                    value={mobile2}
                                    onChange={(e) => {
                                        const value = e.currentTarget.value;
                                        if (!/^\d*$/.test(value)) return;
                                        if (value.length === 1 && value !== '0') return;
                                        if (value.length > 10) return;
                                        setMobile2(value);
                                    }}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.mobile', { defaultValue: '0XXXXXXXXX' })}
                                    type="tel"
                                    disabled={isReadOnly}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.home_phone', { defaultValue: 'Home Phone' })}
                                    value={homePhone}
                                    onChange={(e) => {
                                        const value = e.currentTarget.value;
                                        if (!/^\d*$/.test(value)) return;
                                        if (value.length === 1 && value !== '0') return;
                                        if (value.length > 10) return;
                                        setHomePhone(value);
                                    }}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.mobile', { defaultValue: '0XXXXXXXXX' })}
                                    type="tel"
                                    disabled={isReadOnly}
                                />
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.email', { defaultValue: 'Email' })}
                                    value={email}
                                    onChange={(e) => setEmail(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.email', { defaultValue: 'Enter email address' })}
                                    type="email"
                                    disabled={isReadOnly}
                                />
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.rfid_card', { defaultValue: 'RFID Card' })}
                                    value={rfidCard}
                                    onChange={(e) => setRfidCard(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.rfid_card', { defaultValue: 'Enter RFID card number' })}
                                    disabled={isReadOnly}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.postal_code', { defaultValue: 'Postal Code' })}
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.postal_code', { defaultValue: 'Enter postal code' })}
                                    disabled={isReadOnly}
                                />
                            </Column>
                            <Column>
                                <Inline space="small" alignY={"bottom"}>
                                    <Box>
                                        <Dropdown
                                            label={t('page.approve_mobile_app_customer.form.field.city', { defaultValue: 'City *' })}
                                            value={cityId}
                                            onChange={(e) => setCityId(e.currentTarget.value)}
                                            disabled={isReadOnly}
                                        >
                                            {ciCities.map((ct) => (
                                                <option key={ct.id} value={ct.id.toString()}>
                                                    {ct.city_ln1}
                                                </option>
                                            ))}
                                        </Dropdown>
                                    </Box>
                                    {!isReadOnly && (
                                        <Box marginLeft="xsmall">
                                            <Button
                                                onClick={() => setCityDialogOpen(true)}
                                                variant="ghost"
                                                icon={<IconAdd />}
                                            >
                                                add city
                                            </Button>
                                        </Box>
                                    )}
                                </Inline>
                            </Column>
                        </Columns>
                    </Stack>

                    <Divider />

                    {/* Account Information */}
                    <Stack space="medium">
                        <Text weight="strong">{t('page.approve_mobile_app_customer.form.section.account_information', { defaultValue: 'Account Information' })}</Text>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.credit_limit', { defaultValue: 'Credit Limit' })}
                                    value={creditLimit}
                                    onChange={(e) => {
                                        let value = e.currentTarget.value;
                                        value = value.replace(/[^0-9]/g, '');
                                        if (value.startsWith('0')) {
                                            value = value.replace(/^0+/, '');
                                        }
                                        setCreditLimit(value);
                                    }}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.credit_limit', { defaultValue: 'Enter credit limit' })}
                                    type="number"
                                    disabled={isReadOnly}
                                />
                            </Column>
                            <Column>
                                <DateField
                                    label={t('page.approve_mobile_app_customer.form.field.credit_limit_date', { defaultValue: 'Credit Limit Date' })}
                                    onlyForToday={false}
                                    onChange={(value) => setCreditLimitDate(value || '')}
                                    value={creditLimitDate ? creditLimitDate.slice(0, 10) : ''}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                                    disabled={isReadOnly}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                {mode !== 'add' && (
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.status', { defaultValue: 'Status *' })}
                                        value={status}
                                        onChange={(e) => setStatus(e.currentTarget.value)}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.status', { defaultValue: 'Enter status' })}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </Column>
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.inactive_reason', { defaultValue: 'Inactive Reason' })}
                                    value={inactiveReason}
                                    onChange={(e) => setInactiveReason(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.inactive_reason', { defaultValue: 'Enter inactive reason' })}
                                    disabled={isReadOnly}
                                />
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <TextField
                                    label={t('page.approve_mobile_app_customer.form.field.group_id', { defaultValue: 'Group ID' })}
                                    value={groupId}
                                    onChange={(e) => setGroupId(e.currentTarget.value)}
                                    placeholder={t('page.approve_mobile_app_customer.form.placeholder.group_id', { defaultValue: 'Enter group ID' })}
                                    disabled={isReadOnly}
                                />
                            </Column>
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.sms_status', { defaultValue: 'SMS Status' })}
                                    value={smsStatus}
                                    onChange={(e) => setSmsStatus(e.currentTarget.value)}
                                    disabled={isReadOnly}
                                >
                                    <option value="1">{t('page.approve_mobile_app_customer.form.option.enabled', { defaultValue: 'Enabled' })}</option>
                                    <option value="0">{t('page.approve_mobile_app_customer.form.option.disabled', { defaultValue: 'Disabled' })}</option>
                                </Dropdown>
                            </Column>
                        </Columns>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.dp_app_status', { defaultValue: 'DP App Status' })}
                                    value={dpAppStatus}
                                    onChange={(e) => setDpAppStatus(e.currentTarget.value)}
                                    disabled={isReadOnly}
                                >
                                    <option value="1">{t('page.approve_mobile_app_customer.form.option.active', { defaultValue: 'Active' })}</option>
                                    <option value="0">{t('page.approve_mobile_app_customer.form.option.inactive', { defaultValue: 'Inactive' })}</option>
                                </Dropdown>
                            </Column>
                            <Column>
                                <Dropdown
                                    label={t('page.approve_mobile_app_customer.form.field.pos_app_status', { defaultValue: 'POS App Status' })}
                                    value={posAppStatus}
                                    onChange={(e) => setPosAppStatus(e.currentTarget.value)}
                                    disabled={isReadOnly}
                                >
                                    <option value="1">{t('page.approve_mobile_app_customer.form.option.active', { defaultValue: 'Active' })}</option>
                                    <option value="0">{t('page.approve_mobile_app_customer.form.option.inactive', { defaultValue: 'Inactive' })}</option>
                                </Dropdown>
                            </Column>
                        </Columns>
                    </Stack>

                    <Divider />

                    {/* Image Uploads */}
                    <Stack space="medium">
                        <Text weight="strong">{t('page.approve_mobile_app_customer.form.section.images', { defaultValue: 'Images' })}</Text>

                        <Columns space="small" collapseBelow="tablet">
                            <Column>
                                <Card>
                                    <Stack space="small">
                                        <Text weight="strong" size="small">{t('page.approve_mobile_app_customer.form.field.profile_image', { defaultValue: 'Profile Image' })}</Text>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleProfileImageChange}
                                            disabled={isReadOnly}
                                        />
                                        {/* <Text size="xsmall" tone="secondary">
                                            {t('page.approve_mobile_app_customer.form.images.max_size', { defaultValue: 'Max size: 2MB' })}
                                        </Text> */}
                                        {profileImagePreview ? (
                                            <Box
                                                onClick={() => openImageDialog(profileImagePreview, 'Profile Image')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Stack space="small">
                                                    <Text size="small" tone="secondary">
                                                        {t('page.approve_mobile_app_customer.form.images.selected', {
                                                            defaultValue: 'Selected: {{name}}',
                                                            name: profileImage ? profileImage.name : 'Preview'
                                                        })}
                                                    </Text>
                                                    <img src={profileImagePreview} onError={() => setProfileImagePreview(null)} alt="Profile preview" style={{ width: '100%' }} />
                                                </Stack>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                </Card>
                            </Column>
                            <Column>
                                <Card>
                                    <Stack space="small">
                                        <Text weight="strong" size="small">{t('page.approve_mobile_app_customer.form.field.signature_image', { defaultValue: 'Signature Image' })}</Text>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleSignatureImageChange}
                                            disabled={isReadOnly}
                                        />
                                        {/* <Text size="xsmall" tone="secondary">
                                            {t('page.approve_mobile_app_customer.form.images.max_size', { defaultValue: 'Max size: 2MB' })}
                                        </Text> */}
                                        {signatureImagePreview ? (
                                            <Box
                                                onClick={() => openImageDialog(signatureImagePreview, 'Signature Image')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Stack space="small">
                                                    <Text size="small" tone="secondary">
                                                        {t('page.approve_mobile_app_customer.form.images.selected', {
                                                            defaultValue: 'Selected: {{name}}',
                                                            name: signatureImage ? signatureImage.name : 'Preview'
                                                        })}
                                                    </Text>
                                                    <img src={signatureImagePreview} onError={() => setSignatureImagePreview(null)} alt="Signature preview" style={{ width: '100%' }} />
                                                </Stack>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                </Card>
                            </Column>
                        </Columns>
                    </Stack>

                    <Divider />

                            </>)}
                    {/* Action Buttons */}
                    <Actions>
                        {mode === 'approve' ? (
                            <>
                                <Button
                                    tone="brandAccent"
                                    onClick={handleApproveClick}
                                    loading={updateLoading}
                                >
                                    {t('page.approve_mobile_app_customer.form.button.approve', { defaultValue: 'Approve' })}
                                </Button>
                                <Button
                                    tone="critical"
                                    onClick={handleRejectClick}
                                >
                                    {t('page.approve_mobile_app_customer.form.button.reject', { defaultValue: 'Reject' })}
                                </Button>
                            </>
                        ) : (
                            (mode !== 'add' || showRemaining) && (
                                <>
                                    <Button
                                        type="submit"
                                        tone="brandAccent"
                                        loading={updateLoading}
                                    >
                                        {mode === 'update'
                                            ? t('page.approve_mobile_app_customer.form.button.next', { defaultValue: 'Update Member' })
                                            : t('page.approve_mobile_app_customer.form.button.next', { defaultValue: 'Next' })}
                                    </Button>
                                    {onCancel && (
                                        <Button
                                            variant="ghost"
                                            onClick={onCancel}
                                        >
                                            {t('page.approve_mobile_app_customer.form.button.cancel', { defaultValue: 'Cancel' })}
                                        </Button>
                                    )}
                                </>
                            )
                        )}
                    </Actions>
                </Stack>
            </form>

            <Dialog
                id="image-preview-dialog"
                title={dialogImage?.title || t('page.approve_mobile_app_customer.form.dialog.image_preview.title', { defaultValue: 'Image Preview' })}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            >
                <Stack space="medium">
                    {dialogImage && (
                        <Box>
                            <img
                                src={dialogImage.url}
                                alt={dialogImage.title}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                    )}
                    <Actions>
                        <Button onClick={() => setDialogOpen(false)}>
                            {t('page.approve_mobile_app_customer.form.button.close', { defaultValue: 'Close' })}
                        </Button>
                    </Actions>
                </Stack>
            </Dialog>

            <Dialog
                id="reject-dialog"
                title={t('page.approve_mobile_app_customer.form.dialog.reject.title', { defaultValue: 'Reject Member' })}
                open={rejectDialogOpen}
                onClose={handleCancelReject}
            >
                <Stack space="large">
                    <Text>
                        {t('page.approve_mobile_app_customer.form.dialog.reject.message', {
                            defaultValue: 'Are you sure you want to reject this member? Please provide a reason for rejection.'
                        })}
                    </Text>
                    <Textarea
                        label={t('page.approve_mobile_app_customer.form.field.reject_reason', { defaultValue: 'Reason for Rejection' })}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.currentTarget.value)}
                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.reject_reason', { defaultValue: 'Enter reason for rejection...' })}
                    />
                    <Actions>
                        <Button tone="critical" onClick={handleConfirmReject}>
                            {t('page.approve_mobile_app_customer.form.button.confirm_reject', { defaultValue: 'Confirm Reject' })}
                        </Button>
                        <Button variant="ghost" onClick={handleCancelReject}>
                            {t('page.approve_mobile_app_customer.form.button.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                    </Actions>
                </Stack>
            </Dialog>

            <Dialog
                id="add-city-dialog"
                title={t('page.approve_mobile_app_customer.form.dialog.city.title', { defaultValue: 'Add City' })}
                open={cityDialogOpen}
                onClose={() => setCityDialogOpen(false)}
            >
                <Stack space="large">
                    <Dropdown
                        label={t('page.approve_mobile_app_customer.form.dialog.city.field.district', { defaultValue: 'District *' })}
                        value={newDistrict}
                        onChange={(e) => setNewDistrict(e.currentTarget.value)}
                    >
                        {districts.map((d) => (
                            <option key={d} value={d}>
                                {d === '' ? t('page.approve_mobile_app_customer.form.dialog.city.placeholder.select_district', { defaultValue: 'Select District' }) : d}
                            </option>
                        ))}
                    </Dropdown>
                    <TextField
                        label={t('page.approve_mobile_app_customer.form.dialog.city.field.name', { defaultValue: 'City Name *' })}
                        value={newCityName}
                        onChange={(e) => setNewCityName(e.currentTarget.value)}
                        placeholder={t('page.approve_mobile_app_customer.form.dialog.city.placeholder.name', { defaultValue: 'Enter city name' })}
                    />
                    <Box display={"flex"} justifyContent="flexEnd">
                        <Actions>
                            <Button variant="ghost" onClick={() => setCityDialogOpen(false)}>
                                {t('page.approve_mobile_app_customer.form.button.cancel', { defaultValue: 'Cancel' })}
                            </Button>
                            <Button tone="brandAccent" onClick={handleCreateCity} loading={creatingCity}>
                                {t('page.approve_mobile_app_customer.form.button.create', { defaultValue: 'Create' })}
                            </Button>
                        </Actions>
                    </Box>
                </Stack>
            </Dialog>

            {/* Customer Docs/Info Drawer */}
            <Drawer
                id="customer-docs-info-drawer"
                title={t('page.approve_mobile_app_customer.form.drawer.docs_info_title', { defaultValue: 'Customer Information & Documents' })}
                open={docsInfoDrawerOpen}
                width="large"
                onClose={() => setDocsInfoDrawerOpen(false)}
            >
                {createdCustomerId && createdCustomerTypeId && (
                    <CustomerDocsInfoForm
                        customerId={createdCustomerId}
                        customerTypeId={createdCustomerTypeId}
                        onSuccess={() => {
                            setDocsInfoDrawerOpen(false);
                            if (onSuccess) {
                                onSuccess();
                            }
                        }}
                        onCancel={() => setDocsInfoDrawerOpen(false)}
                    />
                )}
            </Drawer>

            {/* NIC Verification Drawer */}
            {nicRecord && (
                <NicVerificationDrawer
                    open={nicDrawerOpen}
                    record={nicRecord}
                    onClose={() => setNicDrawerOpen(false)}
                    onVerified={handleNicVerified}
                />
            )}
        </Box>
    );
};

export default AddMemberForm;
